import { z } from "zod";
import { getPlatformApiBaseUrl } from "~/lib/env";

const evmAddressSchema = z
  .string()
  .regex(/^0x[a-fA-F0-9]{40}$/u, "Expected an EVM address.");

const siweChallengeSchema = z.object({
  chainId: z.number().int().positive(),
  domain: z.string().min(1),
  expiresAt: z.string().min(1),
  issuedAt: z.string().min(1),
  message: z.string().min(1),
  nonce: z.string().min(1),
  uri: z.string().min(1),
});

const siweSessionSchema = z.object({
  accessToken: z.string().min(1),
  chainId: z.number().int().positive(),
  expiresAt: z.string().min(1),
  expiresIn: z.number().int().nonnegative(),
  refreshToken: z.string().min(1),
  sessionId: z.string().min(1),
  tokenType: z.literal("Bearer"),
  walletAddress: evmAddressSchema,
});

const revokeSiweSessionSchema = z.object({
  revoked: z.literal(true),
});

export type SiweChallenge = z.infer<typeof siweChallengeSchema>;
export type SiweAuthSession = z.infer<typeof siweSessionSchema>;

export class SiweAuthRequestError extends Error {
  readonly status: number | null;

  constructor(message: string, status: number | null) {
    super(message);
    this.name = "SiweAuthRequestError";
    this.status = status;
  }
}

type SiweFetchOptions = {
  readonly fetchImpl?: typeof fetch;
  readonly signal?: AbortSignal;
};

function buildSiweAuthUrl(path: string): string {
  const baseUrl = getPlatformApiBaseUrl().replace(/\/+$/u, "");
  return `${baseUrl}/v1/auth/siwe${path}`;
}

async function fetchSiweAuth<T>(input: {
  readonly body: unknown;
  readonly fetchImpl?: typeof fetch;
  readonly path: string;
  readonly schema: z.ZodType<T>;
  readonly signal?: AbortSignal;
}): Promise<T> {
  const fetchImpl = input.fetchImpl ?? fetch;
  let response: Response;
  try {
    response = await fetchImpl(buildSiweAuthUrl(input.path), {
      body: JSON.stringify(input.body),
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
      signal: input.signal,
    });
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      throw error;
    }

    throw new SiweAuthRequestError("SIWE auth request failed.", null);
  }

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    throw new SiweAuthRequestError(
      text.trim() || `SIWE auth request failed with ${response.status}.`,
      response.status,
    );
  }

  const rawJson = (await response.json()) as unknown;
  const parsed = input.schema.safeParse(rawJson);
  if (!parsed.success) {
    throw new SiweAuthRequestError(
      "Invalid SIWE auth response payload.",
      response.status,
    );
  }

  return parsed.data;
}

export async function requestSiweChallenge(
  input: {
    readonly address: string;
    readonly chainId: number;
  } & SiweFetchOptions,
): Promise<SiweChallenge> {
  return await fetchSiweAuth({
    body: {
      address: input.address,
      chainId: input.chainId,
    },
    fetchImpl: input.fetchImpl,
    path: "/challenge",
    schema: siweChallengeSchema,
    signal: input.signal,
  });
}

export async function verifySiweChallenge(
  input: {
    readonly message: string;
    readonly signature: string;
  } & SiweFetchOptions,
): Promise<SiweAuthSession> {
  return await fetchSiweAuth({
    body: {
      message: input.message,
      signature: input.signature,
    },
    fetchImpl: input.fetchImpl,
    path: "/verify",
    schema: siweSessionSchema,
    signal: input.signal,
  });
}

export async function refreshSiweAuthSession(
  input: {
    readonly refreshToken: string;
  } & SiweFetchOptions,
): Promise<SiweAuthSession> {
  return await fetchSiweAuth({
    body: {
      refreshToken: input.refreshToken,
    },
    fetchImpl: input.fetchImpl,
    path: "/refresh",
    schema: siweSessionSchema,
    signal: input.signal,
  });
}

export async function revokeSiweAuthSession(
  input: {
    readonly accessToken: string;
    readonly sessionId?: string | null;
  } & SiweFetchOptions,
): Promise<void> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const response = await fetchImpl(buildSiweAuthUrl("/revoke"), {
    body: JSON.stringify({
      sessionId: input.sessionId ?? undefined,
    }),
    credentials: "include",
    headers: {
      Authorization: `Bearer ${input.accessToken}`,
      "Content-Type": "application/json",
    },
    method: "POST",
    signal: input.signal,
  });

  if (!response.ok) {
    throw new Error(`SIWE session revoke failed with ${response.status}.`);
  }

  const rawJson = (await response.json()) as unknown;
  const parsed = revokeSiweSessionSchema.safeParse(rawJson);
  if (!parsed.success) {
    throw new Error("Invalid SIWE revoke response payload.");
  }
}
